import type {
  PlanDetail,
  PlanPackageMembershipReplaceRequest,
  PlanPackageMembershipReplaceResult,
  PlanReadPage,
} from '../api/active/generated/plan';
import type { ActiveTransport } from '../api/active/transport';
import {
  notifyQuoteCatalogMutation,
  quoteCatalogSessionResource,
} from '../quote-integration/catalogSessionResource';
import type { PlanMembershipPort } from './planMembershipPort';

export class PlanMembershipApiError extends Error {
  constructor(
    readonly status: number | null,
    readonly code: string | undefined,
    message: string,
    readonly currentResourceVersion?: string | null,
  ) {
    super(message);
  }
}

async function problem(response: Response): Promise<PlanMembershipApiError> {
  let value: {
    code?: string;
    errorCode?: string;
    detail?: string;
    title?: string;
    currentResourceVersion?: string | null;
  } = {};
  try {
    value = (await response.json()) as typeof value;
  } catch {
    // Keep the HTTP status when an upstream proxy returns no problem body.
  }
  const detail = value.detail ?? value.title;
  const message =
    detail === 'PACKAGE_REVISION_NOT_FOUND'
      ? '方案包含不可用的历史套餐版本，请重新打开方案后保存；系统会保留套餐数量并绑定当前已发布版本。'
      : (detail ?? `方案关系请求失败（HTTP ${response.status}）。`);
  return new PlanMembershipApiError(
    response.status,
    value.code ?? value.errorCode,
    message,
    value.currentResourceVersion,
  );
}

export function createPlanMembershipApi(
  transport: ActiveTransport,
  csrfToken: string | null,
): PlanMembershipPort & {
  get(
    planId: string,
    signal?: AbortSignal,
  ): Promise<{ value: PlanDetail; etag: string }>;
} {
  const get = async (planId: string, signal?: AbortSignal) => {
    const response = await transport.requestResponse(
      `/plans/${encodeURIComponent(planId)}`,
      { signal },
    );
    if (!response.ok) throw await problem(response);
    return {
      value: (await response.json()) as PlanDetail,
      etag: response.headers.get('ETag') ?? '',
    };
  };
  return {
    get,
    async list(signal?: AbortSignal) {
      const items: PlanDetail[] = [];
      let page = 1;
      let totalPages = 1;
      do {
        const response = await transport.requestResponse(
          `/plans?page=${page}&pageSize=100`,
          { signal },
        );
        if (!response.ok) throw await problem(response);
        const value = (await response.json()) as PlanReadPage;
        items.push(...value.items);
        totalPages = value.meta.totalPages;
        page += 1;
      } while (page <= totalPages);
      return items;
    },
    async replace(items, idempotencyKey, signal) {
      const mutationScope = quoteCatalogSessionResource.currentScope();
      const body: PlanPackageMembershipReplaceRequest = { items };
      const response = await transport.requestResponse(
        '/plan-package-memberships:replace',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Idempotency-Key': idempotencyKey,
            ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
          },
          body: JSON.stringify(body),
          signal,
        },
      );
      if (!response.ok) throw await problem(response);
      (await response.json()) as PlanPackageMembershipReplaceResult;
      notifyQuoteCatalogMutation(
        mutationScope,
        'plans',
        items.map((item) => item.planId),
        ['packages'],
      );
    },
  };
}
